import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress,
  Paper, Stack, Tab, Tabs, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import api from '../api';

const STATUS_COLORS = { active: 'success', sold: 'warning', deceased: 'error' };

const ShareBar = ({ stats }) => {
  if (!stats) return null;
  const farmers = Object.entries(stats).filter(([k]) => !['Farm', 'total_head'].includes(k));
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
      {farmers.map(([name, data]) => (
        <Paper key={name} variant="outlined" sx={{ p: 2, minWidth: 150, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700}>{name}</Typography>
          <Typography variant="body2" color="text.secondary">{data.head_count} head</Typography>
          <Typography variant="body2" color="text.secondary">
            {data.weighted_share?.toFixed(2)} units
          </Typography>
          <Chip
            size="small"
            color="primary"
            label={`${data.expense_pct?.toFixed(1)}% expenses`}
            sx={{ mt: 0.5 }}
          />
        </Paper>
      ))}
      {stats.Farm != null && (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 150, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700}>Farm</Typography>
          <Typography variant="body2" color="text.secondary">{stats.Farm.head_count} head</Typography>
        </Paper>
      )}
      <Paper variant="outlined" sx={{ p: 2, minWidth: 100, textAlign: 'center', bgcolor: 'primary.light', color: 'white' }}>
        <Typography variant="h4" fontWeight={700}>{stats.total_head}</Typography>
        <Typography variant="body2">Total Head</Typography>
      </Paper>
    </Box>
  );
};

export default function HerdManagement() {
  const [stats, setStats] = useState(null);
  const [cows, setCows] = useState([]);
  const [tab, setTab] = useState('active');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchStats = useCallback(async () => {
    const res = await api.get('/cows/stats/');
    setStats(res.data);
  }, []);

  const fetchCows = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await api.get('/cows/', { params: { status } });
      setCows(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchCows(tab); }, [tab, fetchCows]);

  const emptyCopy = `No ${tab} cattle on record.`;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>Herd</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/herd/new')}>
          Add Cow
        </Button>
      </Box>

      <ShareBar stats={stats} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Active" value="active" />
        <Tab label="Sold" value="sold" />
        <Tab label="Deceased" value="deceased" />
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : isMobile ? (
        /* ── Mobile: card list ── */
        cows.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>{emptyCopy}</Typography>
        ) : (
          <Stack spacing={1.5}>
            {cows.map((cow) => (
              <Card key={cow.id} variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => navigate(`/herd/${cow.id}`)}
                  aria-label={`View cow ${cow.cow_number}`}
                >
                  <CardContent sx={{ py: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="h6" fontWeight={700}>{cow.cow_number}</Typography>
                      <Chip size="small" label={cow.status} color={STATUS_COLORS[cow.status] ?? 'default'} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {cow.owner_name} · {cow.sex}{cow.breed ? ` · ${cow.breed}` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      DOB: {cow.dob} · Age: {cow.age} yr · Share: {Number(cow.cow_share).toFixed(2)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        )
      ) : (
        /* ── Desktop: table ── */
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Owner</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Tag</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Sex</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>DOB</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Age</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Share</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {emptyCopy}
                  </TableCell>
                </TableRow>
              ) : (
                cows.map((cow) => (
                  <TableRow
                    key={cow.id}
                    hover
                    tabIndex={0}
                    sx={{
                      cursor: 'pointer',
                      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                    }}
                    onClick={() => navigate(`/herd/${cow.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/herd/${cow.id}`); }
                    }}
                    aria-label={`View cow ${cow.cow_number}`}
                  >
                    <TableCell>{cow.owner_name}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{cow.cow_number}</TableCell>
                    <TableCell>{cow.sex}</TableCell>
                    <TableCell>{cow.dob}</TableCell>
                    <TableCell>{cow.age} yr</TableCell>
                    <TableCell>
                      <Tooltip title="Expense weight (0–1)">
                        <span>{Number(cow.cow_share).toFixed(2)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={cow.status} color={STATUS_COLORS[cow.status] ?? 'default'} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
